import { Box, Button, Container, Grid, Stack, Typography } from '@mui/material';
import {
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { getAds, getPreferences } from '@app/adapter/user-service';
import { CategoryIconButton } from '@app/components/Category/CategoryIconButton';
import { HomeCarousel } from '@app/components/Home/HomeCarousel';
import {
  AttributesModal,
  ConditionProps as AttributesConditionProps,
  tabs as AttributesModalTabs,
} from '@app/components/Search/AttributesModal';
import {
  ConditionProps as DateTimeConditionProps,
  DateTimeModal,
} from '@app/components/Search/DateTimeModal';
import {
  ConditionProps as LocationsConditionProps,
  LocationsModal,
} from '@app/components/Search/LocationsModal';
import { SearchBox } from '@app/components/Search/SearchBox';
import { SearchTypeTab } from '@app/components/Search/SearchTypeTab';
import { Advertising } from '@app/components/Shared/Advertising';
import { BottomMenu } from '@app/components/Shared/BottomMenu';
import { HeadBlock } from '@app/components/Shared/HeadBlock';
import { TopNavPortal } from '@app/components/TopNav/TopNavPortal';
import { userAuthInfoSelector } from '@app/domain/app';
import { categorySelector, searchConditionState } from '@app/domain/catalog';
import { preferenceSearchConditionsState } from '@app/domain/user';
import { useDeviceType } from '@app/hooks/useBrowserHooks';
import { BOTTOM_MENU_ITEMS } from '@app/static/constants';
import { Ad } from '@app/types/ad';
import { CategoryName } from '@app/types/catalog';
import { PreferenceKeys } from '@app/types/user';
import { findByGroupOrDefault } from '@app/utils/ad';
import { getSearchResultUrl } from '@app/utils/catalog';
import { isError } from '@app/utils/error';
import { unescapeHtml } from '@app/utils/pattern';

export function Home(): ReactElement {
  const navigate = useNavigate();
  const { isDesktop } = useDeviceType();
  const authInfo = useRecoilValue(userAuthInfoSelector);
  const setConditionState = useSetRecoilState(searchConditionState);
  const categories = useRecoilValue(categorySelector);
  const [prefSearchConditionsState, setPrefSearchConditionsState] =
    useRecoilState(preferenceSearchConditionsState);

  const [tab, setTab] = useState<string>(CategoryName.SPOT);
  const tabId = useMemo(() => {
    return categories?.find((c) => c.name === tab)?.id || '';
  }, [categories, tab]);

  const onChangeTab = useCallback(
    (selectedTab: string) => {
      setTab(selectedTab);
    },
    [setTab]
  );

  const fetchPreference = useCallback(async () => {
    if (!authInfo) return;

    try {
      const preference = await getPreferences(
        authInfo.userId,
        authInfo.accessToken,
        authInfo.fingerPrint,
        {
          key: PreferenceKeys.SEARCH_CONDITION,
          limit: 1,
        }
      );
      if (preference.data) {
        setPrefSearchConditionsState(
          preference.data.value.map((p) => {
            return { ...p, value: unescapeHtml(p.value) };
          })
        );
      }
    } catch (e) {
      if (isError(e)) {
        throw new Error(e.message);
      }
    }
  }, [authInfo, setPrefSearchConditionsState]);

  const adGroups = useMemo(() => {
    return isDesktop ? ['106', '107'] : ['101', '102', '103'];
  }, [isDesktop]);

  const [ads, setAds] = useState<Ad[]>([]);
  const fetchAds = useCallback(async () => {
    try {
      const values = await Promise.all(
        adGroups.map(async (group) => {
          const result = await getAds({
            filter: { publicationGroup: [group] },
            top: 1,
          });
          return result.data.value[0];
        })
      );
      setAds(values);
    } catch (e) {
      if (isError(e)) {
        throw new Error(e.message);
      }
    }
  }, [adGroups]);

  useEffect(() => {
    void fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  useEffect(() => {
    void fetchPreference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isLocationModal, setIsLocationModal] = useState(false);
  const searchLocation = useCallback(
    (conditions: LocationsConditionProps) => {
      setConditionState(null);
      navigate(
        getSearchResultUrl({
          categoryId: tabId,
          locationIds: conditions.locationIds,
        })
      );
      setIsLocationModal(false);
    },
    [navigate, setConditionState, tabId]
  );

  const [isAttributeModal, setIsAttributeModal] = useState(false);
  const searchAttribute = useCallback(
    (conditions: AttributesConditionProps) => {
      setConditionState(null);
      navigate(
        getSearchResultUrl({
          attributeIds: conditions.attributes,
          categoryId: tabId,
          variant: {
            max: conditions.max,
            min: conditions.min,
            type: conditions.type,
          },
        })
      );
      setIsAttributeModal(false);
    },
    [navigate, setConditionState, tabId]
  );

  const [isDateTimeModal, setIsDateTimeModal] = useState(false);
  const searchDateTime = useCallback(
    (conditions: DateTimeConditionProps) => {
      setConditionState(null);
      navigate(
        getSearchResultUrl({
          categoryId: tabId,
          work: {
            days: tab === CategoryName.SPOT ? conditions.days : [],
            timeFrom: conditions.timeFrom,
            timeTo: conditions.timeTo,
            weeks: conditions.weeks,
          },
        })
      );
      setIsLocationModal(false);
    },
    [navigate, setConditionState, tab, tabId]
  );

  const [attributesModalTab, setAttributesModalTab] = useState<string>('');
  const openAttributeModal = useCallback((tabName: string) => {
    setAttributesModalTab(tabName);
    setIsAttributeModal(true);
  }, []);

  const onKeywordSearch = useCallback(
    (keyword: string) => {
      setConditionState(null);
      navigate(
        getSearchResultUrl({
          categoryId: tabId,
          keyword,
        })
      );
    },
    [navigate, setConditionState, tabId]
  );

  return (
    <>
      <HeadBlock />
      <TopNavPortal />
      <HomeCarousel />
      <Container
        maxWidth="lg"
        disableGutters={!isDesktop}
        sx={{ pb: '16px!important' }}
      >
        {categories.length > 0 && (
          <SearchTypeTab
            items={categories}
            value={tab}
            onChangeTab={onChangeTab}
          />
        )}
      </Container>
      <Container>
        <Stack spacing={4} width={1}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <CategoryIconButton
                name={`${
                  tab === CategoryName.SPOT ? '日付' : '曜日'
                }・時間から探す`}
                icon={{
                  size: 'large',
                  type: 'material',
                  value: 'EventNote',
                }}
                onClick={() => setIsDateTimeModal(true)}
              />
            </Grid>
            <Grid item xs={4}>
              <CategoryIconButton
                name="エリアから探す"
                icon={{
                  size: 'large',
                  type: 'material',
                  value: 'FmdGoodOutlined',
                }}
                onClick={() => setIsLocationModal(true)}
              />
            </Grid>
            <Grid item xs={4}>
              <CategoryIconButton
                name="診療科から探す"
                icon={{
                  size: 'large',
                  type: 'materialSymbols',
                  value: 'stethoscope',
                }}
                onClick={() => openAttributeModal(AttributesModalTabs[0].value)}
              />
            </Grid>
            <Grid item xs={12} md={authInfo ? 6 : 12}>
              <Button
                color="primary"
                variant="outlined"
                fullWidth
                onClick={() => openAttributeModal(AttributesModalTabs[1].value)}
                sx={{
                  justifyContent: isDesktop ? 'start' : 'center',
                  py: 1.5,
                }}
              >
                <Typography
                  variant="subtitle2"
                  component="span"
                  color="text.primary"
                >
                  詳細条件から探す
                </Typography>
              </Button>
            </Grid>
            {authInfo && (
              <Grid item xs={12} md={6}>
                <Button
                  color="primary"
                  variant="outlined"
                  disabled={!prefSearchConditionsState[0]?.value}
                  fullWidth
                  onClick={() => {
                    setConditionState(null);
                    navigate(
                      `/search/services${prefSearchConditionsState[0]?.value}`
                    );
                  }}
                  sx={{
                    justifyContent: isDesktop ? 'start' : 'center',
                    py: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    component="span"
                    color="text.primary"
                  >
                    保存した条件から探す
                  </Typography>
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <SearchBox onSearch={onKeywordSearch} />
            </Grid>
          </Grid>
          <Stack
            direction={isDesktop ? 'row' : 'column'}
            spacing={2}
            justifyContent="space-around"
          >
            {adGroups.map((group, index) => (
              <Box key={index} textAlign="center" width={1}>
                <Advertising item={findByGroupOrDefault(ads, group)} />
              </Box>
            ))}
          </Stack>
        </Stack>
      </Container>
      <BottomMenu menuItems={BOTTOM_MENU_ITEMS} />
      <Suspense>
        <LocationsModal
          category={tab}
          open={isLocationModal}
          onClose={() => setIsLocationModal(false)}
          onClickSearch={searchLocation}
        />
      </Suspense>
      <Suspense>
        <AttributesModal
          category={tab}
          tabValue={attributesModalTab}
          open={isAttributeModal}
          onClose={() => setIsAttributeModal(false)}
          onClickSearch={searchAttribute}
        />
      </Suspense>
      <Suspense>
        <DateTimeModal
          category={tab}
          open={isDateTimeModal}
          onClose={() => setIsDateTimeModal(false)}
          onClickSearch={searchDateTime}
        />
      </Suspense>
    </>
  );
}
